import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { patchOf } from "../common/patch.js";
import { localDateSchema, moneySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { subscriptionStatusSchema } from "../loyalty/enums.js";
import { consentStateSchema } from "../privacy/consents.js";
import { customerCreatedViaSchema, customerStatusSchema, vehicleConnectorTypeSchema } from "./enums.js";
/**
 * Müşteri profili, araç ve müşteri 360 (Prompt 17, ADR 0017 §4–6).
 *
 * KVKK: `fullName`, `phone`, `email`, `notes` ve araç `plate` kişisel veridir. Çağıranın `customers.personal.view` izni
 * yoksa MASKELİ döner (`personalDataMasked: true`): ad `A*** Y***`, telefon `+90532*****67`, e-posta `a***@ornek.com`,
 * plaka `34*****23`, not `***`. Sadakat kart numarası takma addır (kişisel veri sayılmaz) ve açık döner.
 */
const personal = (description) => z.string().nullable().describe(`KVKK: ${description} (maskeli olabilir)`);
export const customerProfileSchema = z.object({
    id: uuidSchema,
    code: z.string().describe("Müşteri kodu (sıralı: MUS0000001)"),
    fullName: personal("ad soyad"),
    phone: personal("telefon (E.164)"),
    email: personal("e-posta"),
    loyaltyCardNo: z
        .string()
        .nullable()
        .describe("Sadakat kart no (13 hane, Luhn); kasa kartı / QR'ı bununla tanır"),
    counterpartyId: uuidSchema.nullable().describe("Veresiye / kurumsal müşterinin cari bağı"),
    counterpartyName: z.string().nullable(),
    homeStationId: uuidSchema.nullable(),
    homeStationName: z.string().nullable(),
    driverLinked: z.boolean().describe("Müşteri uygulaması hesabına bağlı mı"),
    status: customerStatusSchema,
    createdVia: customerCreatedViaSchema,
    notes: personal("serbest not"),
    anonymizedAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    version: z.number().int(),
    personalDataMasked: z.boolean().describe("Kişisel alanlar maskeli mi (`customers.personal.view` yok)"),
});
export const customerProfilePageSchema = pageOf(customerProfileSchema);
export const listCustomersQuerySchema = pageQuerySchema.extend({
    q: z
        .string()
        .trim()
        .min(2)
        .max(100)
        .optional()
        .describe("Arama: ad, telefon, sadakat kart no, müşteri kodu ya da araç plakası"),
    status: customerStatusSchema.optional(),
    homeStationId: uuidSchema.optional(),
    counterpartyId: uuidSchema.optional(),
});
const customerFields = {
    fullName: z.string().trim().min(2).max(160).nullable().default(null).describe("KVKK: ad soyad"),
    phone: z
        .string()
        .trim()
        .min(7)
        .max(30)
        .nullable()
        .default(null)
        .describe("KVKK: telefon (E.164'e çevrilir)"),
    email: z.string().trim().min(3).max(254).nullable().default(null).describe("KVKK: e-posta"),
    homeStationId: uuidSchema.nullable().default(null),
    counterpartyId: uuidSchema.nullable().default(null),
    notes: z.string().trim().max(1000).nullable().default(null).describe("KVKK: serbest not"),
};
export const createCustomerSchema = z
    .object({
    ...customerFields,
    createdVia: customerCreatedViaSchema.default("WEB"),
    issueLoyaltyCard: z.boolean().default(true).describe("Sadakat kart numarası üret (varsayılan: evet)"),
})
    .refine((value) => value.fullName !== null || value.phone !== null, {
    path: ["fullName"],
    message: "Ad soyad ya da telefon girilmelidir",
});
export const updateCustomerSchema = patchOf(z.object(customerFields)).extend({
    version: z.number().int().positive(),
});
export const customerStatusActionSchema = z.object({
    version: z.number().int().positive(),
    reason: z.string().trim().min(3).max(300).nullable().default(null),
});
export const reissueLoyaltyCardSchema = z.object({ version: z.number().int().positive() });
// ── Araç ────────────────────────────────────────────────────────────────────
export const vehicleSchema = z.object({
    id: uuidSchema,
    plate: z.string().describe("KVKK: normalize plaka (maskeli olabilir)"),
    customerProfileId: uuidSchema.nullable(),
    counterpartyId: uuidSchema.nullable().describe("Filo aracında sahibi cari"),
    fuelProductId: uuidSchema.nullable(),
    fuelProductName: z.string().nullable(),
    connectorType: vehicleConnectorTypeSchema.nullable(),
    brand: z.string().nullable(),
    model: z.string().nullable(),
    modelYear: z.number().int().nullable(),
    status: z.enum(["ACTIVE", "INACTIVE"]),
    notes: z.string().nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    version: z.number().int(),
    personalDataMasked: z.boolean(),
});
export const vehicleListSchema = z.object({ items: z.array(vehicleSchema) });
const vehicleFields = {
    fuelProductId: uuidSchema.nullable().default(null),
    connectorType: vehicleConnectorTypeSchema.nullable().default(null),
    brand: z.string().trim().min(1).max(60).nullable().default(null),
    model: z.string().trim().min(1).max(60).nullable().default(null),
    modelYear: z.number().int().min(1950).max(2100).nullable().default(null),
    notes: z.string().trim().max(500).nullable().default(null),
};
export const createCustomerVehicleSchema = z.object({
    plate: z.string().trim().min(2).max(20).describe("KVKK: plaka (büyük harf, boşluksuz saklanır)"),
    ...vehicleFields,
});
export const updateVehicleSchema = patchOf(z.object({ plate: z.string().trim().min(2).max(20), ...vehicleFields })).extend({ version: z.number().int().positive() });
export const vehicleActionSchema = z.object({ version: z.number().int().positive() });
export const customerVehicleParamsSchema = z.object({ id: uuidSchema, vehicleId: uuidSchema });
// ── Müşteri 360 ─────────────────────────────────────────────────────────────
export const customerOverviewQuerySchema = z
    .object({
    from: localDateSchema.optional().describe("Varsayılan: bitişten 365 gün önce"),
    to: localDateSchema.optional().describe("Varsayılan: bugün (İstanbul)"),
})
    .refine((value) => !value.from || !value.to || value.from <= value.to, {
    path: ["from"],
    message: "Başlangıç bitişten sonra olamaz",
});
export const customerOverviewSchema = z.object({
    customer: customerProfileSchema,
    period: z.object({ from: localDateSchema, to: localDateSchema }),
    spending: z.object({
        total: moneySchema.describe("Dönemdeki net harcama (iade / iptal karşı kayıtları dâhil defter toplamı)"),
        saleCount: z.number().int().describe("Tamamlanmış satış sayısı (tam iptal edilenler hariç)"),
        returnCount: z.number().int().describe("İade / iptal karşı kaydı sayısı"),
        byBusinessUnit: z.array(z.object({
            businessUnit: z.string().nullable().describe("FUEL, LPG, MARKET… (bilinmiyorsa null)"),
            net: moneySchema,
            lineCount: z.number().int(),
        })),
    }),
    visits: z.object({
        count: z.number().int().describe("Ziyaret = satış yapılan farklı İstanbul günü"),
        perMonth: z.string().describe("30 günde ortalama ziyaret (2 ondalık)"),
        firstVisitAt: timestampSchema.nullable(),
        lastVisitAt: timestampSchema.nullable(),
    }),
    preferredStation: z
        .object({ stationId: uuidSchema, stationName: z.string(), visitCount: z.number().int() })
        .nullable()
        .describe("En çok ziyaret edilen istasyon (eşitlikte son ziyaret)"),
    loyalty: z
        .object({
        pointsBalance: z.string().describe("Puan (tam sayı dizge)"),
        pendingPoints: z.string(),
        tierCode: z.string().nullable(),
        tierName: z.string().nullable(),
    })
        .nullable()
        .describe("Sadakat hesabı yoksa null"),
    vehicles: z.array(vehicleSchema),
    subscriptions: z.array(z.object({
        id: uuidSchema,
        planId: uuidSchema,
        planCode: z.string(),
        planName: z.string(),
        status: subscriptionStatusSchema,
        currentPeriodStart: localDateSchema,
        currentPeriodEnd: localDateSchema,
        vehicleId: uuidSchema.nullable(),
    })),
    consent: consentStateSchema,
});
// ── Müşteri uygulaması bağlantı daveti (ADR 0017 §3) ────────────────────────
export const LINK_INVITATION_CHANNELS = ["QR", "SMS"];
export const linkInvitationChannelSchema = z.enum(LINK_INVITATION_CHANNELS);
export const createLinkInvitationSchema = z.object({ channel: linkInvitationChannelSchema });
export const linkInvitationSchema = z.object({
    invitationId: uuidSchema,
    customerProfileId: uuidSchema,
    channel: linkInvitationChannelSchema,
    token: z.string().describe("İmzalı, tek kullanımlık davet belirteci (15 dk)"),
    deepLink: z.string().describe("Müşteri uygulamasının açacağı bağlantı"),
    qrPayload: z.string().describe("QR içeriği (= deepLink)"),
    expiresAt: timestampSchema,
    smsSentTo: z.string().nullable().describe("SMS kanalında maskeli alıcı"),
});
//# sourceMappingURL=customers.js.map